import type { ChartQueryPanel } from 'features/charts-v2/types/chart-query-panel'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { augmentMetricsForWeighting } from 'features/charts-v2/utils/augment-metrics-for-weighting'
import { buildRequestPanel } from 'features/charts-v2/utils/build-request-panel'
import { normalizeLegacyPanelSearch } from 'features/charts-v2/utils/normalize-legacy-panel-search'

/**
 * Augment the request with any hidden weight metric the summary totals need
 * (e.g. sentence_count for sentiment), so they can compute a grain-correct
 * weighted average. The augmented metrics stay server-side only — the panel
 * (and the UI state) is never mutated. The same util drives the totals math,
 * so the hidden weight column's positional index is identical on both seams.
 * Returns the same panel object when nothing is added.
 */
function withWeightingMetrics(panel: ChartPanel): ChartPanel {
  const augmentedMetrics = augmentMetricsForWeighting(panel.metrics)
  return augmentedMetrics.length === panel.metrics.length
    ? panel
    : { ...panel, metrics: augmentedMetrics }
}

/**
 * The panel the legacy `/charts/chart` route posts for a chart panel. The one
 * implementation of that lowering: `useChartQuery` posts it, and a dashboard
 * tile's Ask Yogi snapshot reports it, so the two cannot disagree.
 *
 * Order matters. Legacy / corrupted search shapes are healed before the
 * `SEMANTIC` marker is written: a v1 `['semantic']` operator array only
 * becomes `smartSearchEnabled` in the heal, so injecting first would send a
 * keyword search where the user asked for a semantic one. Saved dashboard
 * panels reach the request without the charts-v2 store's hydrate migration, so
 * a panel persisted as `{ 'Includes all': ['or'] }` (keywords stranded in
 * `searchTerms`) would otherwise search for the literal word "or".
 */
export function buildChartRequestPanel(panel: ChartPanel): ChartQueryPanel {
  return buildRequestPanel(
    normalizeLegacyPanelSearch(withWeightingMetrics(panel)),
  )
}
